import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, Phone, CheckCircle2, Clock, FileText, Users, Building2, Stethoscope, Hospital, AlertCircle, Globe } from "lucide-react";

const AXA_COLOR = "#0066FF";

const AxaTarif = () => {
  const heroFeatures = [
    { icon: Globe, title: "Weltweit", subtitle: "International" },
    { icon: Users, title: "Schulungen", subtitle: "Kostenlos" },
    { icon: Shield, title: "Bis 10 Mio. €", subtitle: "Deckungssumme" }
  ];

  const coverageAreas = [
    {
      icon: Shield,
      title: "Eigenschäden",
      items: [
        "IT-Forensik & Datenwiederherstellung",
        "Betriebsunterbrechung durch IT-Ausfälle",
        "Wiederanlaufkosten und Mehrkosten",
        "Cyber-Erpressung & Ransomware",
        "Kosten für Krisenmanagement"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cyber-Haftpflicht",
      items: [
        "DSGVO-Bußgelder und Schadensersatz",
        "Datenschutzverletzungen & Meldepflichten",
        "Ansprüche Dritter bei Datenlecks",
        "Abwehr unberechtigter Ansprüche",
        "Rechtsschutz bei Cyber-Vorfällen"
      ]
    },
    {
      icon: Phone,
      title: "24/7 Assistance",
      items: [
        "Cyber-Notfall-Hotline rund um die Uhr",
        "Sofortige Expertenunterstützung",
        "Koordination aller Dienstleister",
        "PR- und Krisenkommunikation",
        "Persönlicher Schadenmanager"
      ]
    },
    {
      icon: Globe,
      title: "Prävention & Training",
      items: [
        "Kostenlose Cyber-Schulungen",
        "Mitarbeiter-Sensibilisierung",
        "Cyber-Risiko-Analysen",
        "Best-Practice-Empfehlungen",
        "Regelmäßige Sicherheits-Updates"
      ]
    }
  ];

  const caseStudies = [
    {
      title: "Ransomware-Angriff auf Facharztpraxis",
      description: "WannaCry-Trojaner verschlüsselte Patientendaten und Abrechnungssystem. Praxis musste 5 Tage schließen, 200+ Termine abgesagt.",
      outcome: "AXA-Notfallteam binnen 2 Stunden aktiv. IT-Spezialisten stellten Systeme wieder her, kommunizierten mit Patienten. Versicherung übernahm 58.400 € (IT-Forensik, Datenrettung, Umsatzausfall, Krisenmanagement).",
      damage: "58.400 €"
    },
    {
      title: "DSGVO-Verstoß durch Datenleck",
      description: "Unverschlüsselte E-Mail mit 450 Patientendaten an falschen Empfänger. Meldepflicht innerhalb 72 Stunden, drohende Bußgelder.",
      outcome: "AXA-Datenschutzexperten übernahmen Meldung an Aufsichtsbehörde, rechtliche Beratung und Patientenbenachrichtigung. Bußgeld auf 8.500 € reduziert. Gesamtkosten 31.200 € von Versicherung getragen.",
      damage: "31.200 €"
    },
    {
      title: "Phishing-Attacke auf MVZ",
      description: "CEO-Fraud: Buchhalterin überwies nach gefälschter E-Mail 35.000 € an Betrüger. Zusätzlich wurden Zugangsdaten kompromittiert.",
      outcome: "AXA koordinierte Rückholungsversuche, IT-Forensik und Systemhärtung. 18.000 € Überweisung zurückgeholt. Versicherung übernahm verbleibende 17.000 € plus 24.800 € IT-Kosten. Gesamt: 41.800 €.",
      damage: "41.800 €"
    },
    {
      title: "DDoS-Angriff auf Online-Terminbuchung",
      description: "Distributed-Denial-of-Service-Attacke legte Online-Systeme für 3 Tage lahm. Keine Terminbuchungen möglich, Imageschaden.",
      outcome: "AXA-Partner implementierten DDoS-Schutz, wiederhergestellte Erreichbarkeit. PR-Beratung für Kommunikation. Versicherung trug 27.600 € (IT-Abwehr, Umsatzverlust, Krisenberatung).",
      damage: "27.600 €"
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausarztpraxen",
      description: "Umfassender Schutz für den digitalen Praxisalltag",
      benefits: [
        "Schutz sensibler Patientendaten (Kartei, Labor)",
        "Absicherung gegen Praxissoftware-Ausfälle",
        "DSGVO-Bußgeldschutz bei Datenpannen",
        "24/7-Notfallhilfe bei Cyberangriffen"
      ],
      recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "Facharztpraxen",
      description: "Spezialisierter Schutz für komplexe IT-Systeme",
      benefits: [
        "Schutz hochsensibler Diagnose- und Bilddaten",
        "Absicherung vernetzter Medizingeräte",
        "Kostenübernahme bei IT-Betriebsunterbrechung",
        "Cyber-Krisenmanagement & Reputationsschutz"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
    },
    {
      icon: Building2,
      title: "MVZ & Praxisgemeinschaften",
      description: "Skalierbare Lösungen für mehrere Standorte",
      benefits: [
        "Schutz vernetzter Systeme über Standorte hinweg",
        "Deckung hoher Umsatzausfälle bei IT-Ausfall",
        "Koordiniertes Krisenmanagement für Verbund",
        "Zentrale Verwaltung aller Cyber-Risiken"
      ],
      recommendation: "Empfohlene Deckung: 2.000.000 - 3.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Krankenhäuser",
      description: "Enterprise-Schutz für große Gesundheitseinrichtungen",
      benefits: [
        "Höchste Deckung für kritische Infrastruktur",
        "Schutz vernetzter Medizintechnik (OP, Intensiv)",
        "Absicherung gegen mehrtägige Betriebsausfälle",
        "Spezialisiertes Krisenmanagement für Kliniken"
      ],
      recommendation: "Empfohlene Deckung: 3.000.000 - 10.000.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Selbstbehalt",
      description: "Standard: 2.500 € pro Schadensfall. Optional reduzierbar auf 1.000 € oder erhöhbar auf 5.000 € für niedrigere Prämie."
    },
    {
      icon: CheckCircle2,
      title: "Deckungssummen",
      description: "Flexibel: 250.000 € bis 10.000.000 €. Einzelpraxen: 500.000-1 Mio. €. MVZ/Kliniken: 2-5 Mio. €."
    },
    {
      icon: Phone,
      title: "Service & Support",
      description: "24/7 Cyber-Notfall-Hotline. Persönlicher Schadenmanager. Präventive Sicherheitsschulungen inklusive."
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "Mindestlaufzeit: 1 Jahr mit automatischer Verlängerung. Sonderkündigungsrecht nach Schaden."
    },
    {
      icon: FileText,
      title: "Kündigungsfrist",
      description: "3 Monate zum Vertragsende. Sonderkündigung nach Schadensfall möglich. Beidseitiges Kündigungsrecht."
    },
    {
      icon: Globe,
      title: "Weltweiter Schutz",
      description: "Internationaler Versicherungsschutz. Präventive Schulungen inklusive. Paketrabatte bei Kombination."
    }
  ];

  const faqs = [
    {
      question: "Was macht die AXA Cyberversicherung besonders?",
      answer: "AXA bietet als internationaler Versicherer umfassende Cyber-Expertise mit globalem Netzwerk. Besonders hervorzuheben sind präventive Sicherheitsschulungen, 24/7-Notfallhilfe und weltweiter Versicherungsschutz."
    },
    {
      question: "Welche präventiven Leistungen bietet AXA?",
      answer: "AXA stellt kostenlose Online-Schulungen zur Mitarbeiter-Sensibilisierung bereit, bietet Cyber-Risiko-Analysen und unterstützt bei der Implementierung von Sicherheitsmaßnahmen."
    },
    {
      question: "Wie schnell greift der Versicherungsschutz im Notfall?",
      answer: "Der Versicherungsschutz beginnt sofort ab Vertragsbeginn. Die 24/7-Notfall-Hotline koordiniert binnen Stunden erste Maßnahmen und vermittelt IT-Forensiker."
    },
    {
      question: "Welche Deckungssummen bietet AXA?",
      answer: "AXA bietet flexible Deckungssummen von 250.000 € bis 10 Millionen €. Für Einzelpraxen empfehlen wir 500.000-1.000.000 €, für MVZ und Kliniken 2-5 Millionen €."
    },
    {
      question: "Sind Ransomware-Lösegeldzahlungen versichert?",
      answer: "Ja, AXA übernimmt nach sorgfältiger Prüfung auch Lösegeldzahlungen bei Ransomware-Angriffen. Zusätzlich alle damit verbundenen Kosten wie IT-Forensik und Datenrettung."
    },
    {
      question: "Kann ich die AXA Cyberversicherung mit anderen Policen kombinieren?",
      answer: "Ja, AXA bietet attraktive Paketlösungen bei Kombination mit Berufshaftpflicht, Betriebshaftpflicht oder Inhaltsversicherung. Zentrale Verwaltung über einen Ansprechpartner."
    },
    {
      question: "Gibt es Wartezeiten nach Vertragsabschluss?",
      answer: "Nein, der Versicherungsschutz beginnt sofort ohne Wartezeit. Nur bei bekannten Vorfällen vor Vertragsabschluss besteht kein Schutz."
    }
  ];

  const documents = [
    {
      title: "AXA Cyber Produktinformation",
      type: "PDF, ca. 800 KB",
      url: "/erstinformation.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF, ca. 1,2 MB",
      url: "/erstinformation.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="AXA"
      companyColor={AXA_COLOR}
      productRating={{ value: 4.8, count: 512 }}
      priceRange="60-220"
      pageTitle="AXA Cyberversicherung für Ärzte & Praxen 2025 | Internationaler Schutz mit Prävention"
      metaDescription="AXA Cyberversicherung: Weltweiter Schutz mit präventiven Schulungen & 24/7-Notfallhilfe. Deckung bis 10 Mio. € für Arztpraxen, MVZ & Kliniken."
      metaKeywords="AXA Cyberversicherung, Cyber-Versicherung Ärzte, AXA Praxis Cyberschutz, internationale Cyberversicherung"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/axa"
      heroLabel="Internationale Expertise – Weltweiter Schutz"
      heroTitle="AXA Cyberversicherung für medizinische Einrichtungen"
      heroDescription="Internationaler Schutz mit präventiven Sicherheitsschulungen und weltweiter Notfallhilfe. Als globaler Versicherer bietet AXA umfassende Cyber-Expertise für Ihre medizinische Einrichtung."
      heroFeatures={heroFeatures}
      alertTitle="Warum AXA?"
      alertDescription="Als internationaler Versicherer mit globalem Netzwerk bietet AXA umfassende Cyber-Expertise und weltweiten Schutz. Die kostenlosen präventiven Sicherheitsschulungen und die 24/7-Notfallhilfe machen AXA zur idealen Wahl für digital arbeitende medizinische Einrichtungen."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default AxaTarif;
